import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './modal.css';

function AppleAccounts() {
    // Register all Community features
    ModuleRegistry.registerModules([AllCommunityModule]);

    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Popup and form state
    const [showPopup, setShowPopup] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [formData, setFormData] = useState({
        id: '',
        app_name: '',
        acc_email: '',
        acc_name: '',
        acc_ib: '',
        acc_hold_add: ''
    });

    const [showPopupAdd, setShowPopupAdd] = useState(false);
    const [editingRowAdd, setEditingRowAdd] = useState(null);
    const [formDataAdd, setFormDataAdd] = useState({
        app_name: '',
        acc_email: '',
        acc_name: '',
        acc_ib: '',
        acc_hold_add: ''
    });
    // Fetch data on component mount
    useEffect(() => {
        const fetchAppleData = async () => {
            try {
                const requestOptions = {
                    method: "GET",
                    redirect: "follow",
                    credentials: 'include'
                };

                const response = await fetch("https://apidashboard.mobimartech.com/apples/getApples", requestOptions);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.success && data.result) {
                    setRowData(data.result);
                } else {
                    throw new Error('Invalid API response structure');
                }
            } catch (err) {
                setError(err.message);
                console.error('Fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAppleData();
    }, []);

    // Handle edit button click - memoized with useCallback
    const handleEdit = useCallback((id) => {
        console.log('Edit clicked for ID:', id);
        const row = rowData.find(item => item.id === id);
        if (!row) {
            console.error('Row not found for ID:', id);
            return;
        }

        setFormData({
            id: row.id,
            app_name: row.app_name || '',
            acc_email: row.acc_email || '',
            acc_name: row.acc_name || '',
            acc_ib: row.acc_ib || '',
            acc_hold_add: row.acc_hold_add || '',
        });
        setEditingRow(row);
        setShowPopup(true);
    }, [rowData]); // Dependency on rowData

    const handleAdd = useCallback(() => {
        console.log('Add clicked ');
        setShowPopupAdd(true);
    }, []);

    // Handle popup close
    const handleClose = () => {
        setFormData({
            id: '',
            app_name: '',
            acc_email: '',
            acc_name: '',
            acc_ib: '',
            acc_hold_add: ''
        });
        setEditingRow(null);
        setShowPopup(false);
    };

    const handleCloseAdd = () => {
        setFormDataAdd({
            app_name: '',
            acc_email: '',
            acc_name: '',
            acc_ib: '',
            acc_hold_add: ''
        });
        setShowPopupAdd(false);
    };

    // Handle backdrop click
    const handleBackdropClick = (e) => {
        if (e.target.className === 'custom-popup-backdrop') {
            handleClose();
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleInputAddChange = (e) => {
        const { name, value } = e.target;
        setFormDataAdd(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('Submitting:', formData);

        try {
            const requestOptions = {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
                credentials: 'include',
                redirect: "follow"
            };

            const response = await fetch("https://apidashboard.mobimartech.com/apples/updateAppleServices", requestOptions);
            const result = await response.json(); // Parse as JSON, not text

            if (!response.ok) {
                throw new Error(result.error || `HTTP error! status: ${response.status}`);
            }

            if (result.success) {
                console.log('Update successful:', result);

                // Update local state to reflect changes
                setRowData(prevData =>
                    prevData.map(row =>
                        row.id === formData.id ? { ...row, ...formData } : row
                    )
                );

                // Show success message (optional)
                alert('Apple service updated successfully!');

                // Only close on success
                handleClose();
            } else {
                throw new Error(result.error || 'Update failed');
            }
        } catch (error) {
            console.error('Update error:', error);
            alert(`Failed to update: ${error.message}`);
            // Don't close modal on error - let user try again
        }
    };

    const handleAddService = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch("https://apidashboard.mobimartech.com/apples/addAppleService", {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formDataAdd),
                credentials: 'include'
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to add service');
            }

            // Add new row to table with returned ID
            const newRow = {
                id: result.insertId,
                ...formDataAdd
            };
            setRowData(prev => [...prev, newRow]);

            handleCloseAdd();
        } catch (error) {
            console.error('Error adding service:', error);
            alert(error.message);
        }
    };

    // Column definitions - include handleEdit in dependencies
    const columnDefs = useMemo(() => [
        {
            field: 'id',
            headerName: 'ID',
            sortable: true,
            filter: 'agNumberColumnFilter',
            width: 40
        },
        {
            field: 'app_name',
            headerName: 'App Name',
            sortable: true,
            filter: true,
            width: 150,
        },
        {
            field: 'acc_email',
            headerName: 'Acc Email',
            sortable: true,
            filter: true,
            // flex: 1,
            width: 220
        },
        {
            field: 'acc_name',
            headerName: 'Acc Name',
            sortable: true,
            filter: true,
            width: 150,
        },
        {
            field: 'acc_ib',
            headerName: 'Acc IBAN',
            sortable: true,
            filter: true,
            width: 280,
        },
        {
            field: 'acc_hold_add',
            headerName: 'Acc Holder Address',
            sortable: true,
            filter: true,
            width: 250,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            cellRenderer: (params) => {
                // Use cellRendererParams to pass the handler
                return (
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={() => params.handleEdit(params.data.id)}
                    >
                        Edit
                    </button>
                );
            },
            cellRendererParams: {
                handleEdit: handleEdit
            },
            sortable: false,
            filter: false,
            width: 100
        }
    ], [handleEdit]); // CRITICAL: Include handleEdit in dependencies

    // Default column properties
    const defaultColDef = useMemo(() => ({
        minWidth: 100,
        resizable: true,
        floatingFilter: true,
    }), []);

    if (loading) return <div>Loading Apple accounts...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <>
            <div
                className="col-10"
                style={{
                    position: "relative",
                    left: "220px",
                    top: "120px",
                }}
            >
                <div className='row justify-content-end'>
                    <div className='col-2'>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleAdd()}
                        >
                            Add Service
                        </button>
                    </div>
                </div>
                <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        animateRows={true}
                        pagination={true}
                        paginationPageSize={10}
                        enableCellTextSelection={true}
                    />
                </div>
            </div>

            {/* Custom Popup */}
            {showPopup && (
                <div className="custom-popup-backdrop" onClick={handleBackdropClick}>
                    <div className="custom-popup">
                        <div className="popup-header">
                            <h5>Edit App</h5>
                            <button
                                className="popup-close-btn"
                                onClick={handleClose}
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="popup-body">
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '40px' }}>
                                    <div className="form-group">
                                        <label>ID</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.id}
                                            disabled
                                            readOnly
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>App Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="app_name"
                                            value={formData.app_name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '40px' }}>
                                    <div className="form-group">
                                        <label>ACC Email</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="acc_email"
                                            value={formData.acc_email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Acc Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="acc_name"
                                            value={formData.acc_name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '40px' }}>
                                    <div className="form-group">
                                        <label>Acc IBAN</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="acc_ib"
                                            value={formData.acc_ib}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Holder Address</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="acc_hold_add"
                                            value={formData.acc_hold_add}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="popup-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showPopupAdd && (
                <div className="custom-popup-backdrop" onClick={handleBackdropClick}>
                    <div className="custom-popup">
                        <div className="popup-header">
                            <h5>Add App</h5>
                            <button
                                className="popup-close-btn"
                                onClick={handleCloseAdd}
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleAddService}>
                            <div className="popup-body">
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '40px' }}>
                                    {/* <div className="form-group">
                                        <label>ID</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formDataAdd.id}
                                            disabled
                                            readOnly
                                        />
                                    </div> */}
                                    <div className="form-group">
                                        <label>App Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="app_name"
                                            value={formDataAdd.app_name}
                                            onChange={handleInputAddChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '40px' }}>
                                    <div className="form-group">
                                        <label>ACC Email</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="acc_email"
                                            value={formDataAdd.acc_email}
                                            onChange={handleInputAddChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Acc Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="acc_name"
                                            value={formDataAdd.acc_name}
                                            onChange={handleInputAddChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '40px' }}>
                                    <div className="form-group">
                                        <label>Acc IBAN</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="acc_ib"
                                            value={formDataAdd.acc_ib}
                                            onChange={handleInputAddChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Holder Address</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="acc_hold_add"
                                            value={formDataAdd.acc_hold_add}
                                            onChange={handleInputAddChange}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="popup-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCloseAdd}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}

export default AppleAccounts;